// AWS Cognito Authentication Service
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import axios from 'axios';
import crypto from 'crypto-js';

// AWS Cognito Configuration - Updated for new User Pool
const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || 'eu-west-1_Y9ybUyIbO',
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '1tfcrcch96cov1jn36me1inhgl'
};



// Client secret - should be stored in environment variables in production
const clientSecret = process.env.REACT_APP_COGNITO_CLIENT_SECRET || 'n32t7bukh20fegr3s321rgf3rm3abjiu3nakg4upg4gn5khnf47';

const userPool = new CognitoUserPool(poolData);

// Function to calculate SECRET_HASH
const calculateSecretHash = (username) => {
  if (!clientSecret) return null;
  
  const message = username + poolData.ClientId;
  const hash = crypto.HmacSHA256(message, clientSecret);
  return crypto.enc.Base64.stringify(hash);
};

class AuthService {
  // Sign up new user
  signUp(email, password, firstName, lastName) {
    console.log('Using User Pool:', poolData.UserPoolId);
    console.log('Using Client ID:', poolData.ClientId);
    return new Promise((resolve, reject) => {
      // Use email directly as username since User Pool requires email format
      const username = email;
      
      const attributeList = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
        new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
        new CognitoUserAttribute({ Name: 'name', Value: `${firstName} ${lastName}` })
      ];
      
      // Calculate SECRET_HASH using the username
      const secretHash = calculateSecretHash(username);
      
      userPool.signUp(username, password, attributeList, null, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        
        // Store the email for future reference
        localStorage.setItem('cognito_email', email);
        
        resolve(result);
      });
    });
  }

  // Confirm sign up with verification code
  confirmSignUp(email, verificationCode) {
    return new Promise((resolve, reject) => {
      // Use email directly as username
      const username = email;

      const userData = { Username: username, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmRegistration(verificationCode, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }

  // Sign in user
  signIn(email, password, mfaCode = null) {
    return new Promise((resolve, reject) => {
      // Use email directly for login since User Pool has email alias
      const username = email;
      const secretHash = calculateSecretHash(username);
      
      const authenticationData = {
        Username: username,
        Password: password
      };
      
      if (secretHash) {
        authenticationData.SecretHash = secretHash;
      }
      
      const authenticationDetails = new AuthenticationDetails(authenticationData);
      const userData = { Username: username, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          const accessToken = result.getAccessToken().getJwtToken();
          const idToken = result.getIdToken().getJwtToken();
          const refreshToken = result.getRefreshToken().getToken();
          
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('idToken', idToken);
          localStorage.setItem('refreshToken', refreshToken);
          
          resolve({
            accessToken,
            idToken,
            refreshToken,
            user: result.getIdToken().payload
          });
        },
        onFailure: (err) => {
          reject(err);
        },
        mfaRequired: (challengeName, challengeParameters) => {
          resolve({ requiresMFA: true, cognitoUser, challengeName });
        },
        totpRequired: (challengeName, challengeParameters) => {
          resolve({ requiresMFA: true, cognitoUser, challengeName });
        },
        mfaSetup: (challengeName, challengeParameters) => {
          resolve({ requiresMFA: true, cognitoUser, challengeName: 'MFA_SETUP' });
        },
        customChallenge: (challengeParameters) => {
          resolve({ requiresMFA: true, cognitoUser, challengeName: 'CUSTOM_CHALLENGE' });
        }
      });
    });
  }

  // Confirm MFA code
  confirmMFA(cognitoUser, mfaCode, challengeName = 'SOFTWARE_TOKEN_MFA') {
    return new Promise((resolve, reject) => {
      cognitoUser.sendMFACode(mfaCode, {
        onSuccess: (result) => {
          const accessToken = result.getAccessToken().getJwtToken();
          const idToken = result.getIdToken().getJwtToken();
          const refreshToken = result.getRefreshToken().getToken();
          
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('idToken', idToken);
          localStorage.setItem('refreshToken', refreshToken);
          
          resolve({
            accessToken,
            idToken,
            refreshToken,
            user: result.getIdToken().payload
          });
        },
        onFailure: (err) => {
          reject(err);
        }
      }, challengeName);
    });
  }

  // Setup MFA
  setupMFA() {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.associateSoftwareToken({
          associateSecretCode: (secretCode) => {
            resolve({ secretCode });
          },
          onFailure: (err) => {
            reject(err);
          }
        });
      });
    });
  }

  // Verify MFA setup
  verifyMFASetup(totpCode) {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.verifySoftwareToken(totpCode, 'Trading Journal', {
          onSuccess: (result) => {
            resolve(result);
          },
          onFailure: (err) => {
            reject(err);
          }
        });
      });
    });
  }

  // Sign out user
  signOut() {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    
    // Clear tokens from localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
  }

  // Get current user
  getCurrentUser() {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        if (!session.isValid()) {
          reject(new Error('Session is not valid'));
          return;
        }

        // For federated users, try to extract user info from the ID token
        // instead of calling getUserAttributes which may fail for social logins
        try {
          const idToken = session.getIdToken().getJwtToken();
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          
          // Create user attributes from token claims
          const userAttributes = {
            email: payload.email,
            given_name: payload.given_name || '',
            family_name: payload.family_name || '',
            name: payload.name || '',
            sub: payload.sub
          };
          
          resolve({
            username: cognitoUser.getUsername(),
            attributes: userAttributes,
            session
          });
          return;
        } catch (tokenError) {
          // Fallback to getUserAttributes
        }

        // Fallback to getUserAttributes for non-federated users
        cognitoUser.getUserAttributes((err, attributes) => {
          if (err) {
            console.error('Error getting user attributes:', err);
            // If getUserAttributes fails, still try to return a user object
            // with basic info from the session
            try {
              const idToken = session.getIdToken().getJwtToken();
              const payload = JSON.parse(atob(idToken.split('.')[1]));
              
              resolve({
                username: cognitoUser.getUsername(),
                attributes: {
                  email: payload.email,
                  name: payload.name || ''
                },
                session
              });
            } catch (tokenError) {
              reject(err);
            }
            return;
          }

          const userAttributes = {};
          attributes.forEach(attr => {
            userAttributes[attr.getName()] = attr.getValue();
          });

          resolve({
            username: cognitoUser.getUsername(),
            attributes: userAttributes,
            session
          });
        });
      });
    });
  }

  // Refresh session if needed
  refreshSession() {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        // For federated logins, we might not have a cognitoUser but still have valid tokens
        const idToken = this.getIdToken();
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (idToken) {
          try {
            // Check if token is still valid
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            const timeUntilExpiry = payload.exp - currentTime;
            
            if (timeUntilExpiry > 300) { // If token valid for more than 5 minutes
              // Token still valid
              resolve(false);
              return;
            }
            
            // For federated logins with expired tokens, we need to redirect to login again
            if (refreshToken) {
              // Try to use the refresh token directly with Cognito token endpoint
              this.refreshTokenDirectly(refreshToken, idToken)
                .then(result => {
                  resolve(true);
                })
                .catch(err => {
                  console.error('Failed to refresh federated token:', err);
                  reject(new Error('No user found'));
                });
              return;
            }
          } catch (error) {
            console.error('Error parsing federated token:', error);
          }
        }
        
        reject(new Error('No user found'));
        return;
      }
      
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        reject(new Error('No refresh token found'));
        return;
      }
      
      // Check if token needs refresh
      const idToken = this.getIdToken();
      if (idToken) {
        try {
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = payload.exp - currentTime;
          
          // Get the actual token duration from localStorage or calculate it
          const storedDuration = localStorage.getItem('tokenTotalDuration');
          const actualTokenDuration = storedDuration ? parseFloat(storedDuration) * 3600 : (payload.exp - payload.iat);
          
          // If token is valid for more than 90% of its lifetime, no need to refresh
          const refreshThreshold = actualTokenDuration * 0.1;
          if (timeUntilExpiry > refreshThreshold) {
            // Token still valid
            resolve(false);
            return;
          }
        } catch (error) {
          console.error('Error parsing token during refresh check:', error);
        }
      }
      
      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }
        
        // Use the refresh token to get a new session
        cognitoUser.refreshSession(session.getRefreshToken(), (err, newSession) => {
          if (err) {
            reject(err);
            return;
          }
          
          // Update tokens in localStorage
          const newIdToken = newSession.getIdToken().getJwtToken();
          const newAccessToken = newSession.getAccessToken().getJwtToken();
          
          localStorage.setItem('idToken', newIdToken);
          localStorage.setItem('accessToken', newAccessToken);
          
          // Store the refresh time to avoid excessive refreshes
          localStorage.setItem('lastTokenRefresh', Date.now().toString());
          
          // Session refreshed successfully
          resolve(true);
        });
      });
    });
  }
  
  // Helper method to refresh token directly with Cognito token endpoint
  async refreshTokenDirectly(refreshToken, idToken) {
    try {
      // Parse the ID token to get the username
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const username = payload.email || payload['cognito:username'] || payload.sub;
      
      if (!username) {
        throw new Error('Could not determine username from token');
      }
      
      const tokenEndpoint = 'https://eu-west-1y9ybuyibo.auth.eu-west-1.amazoncognito.com/oauth2/token';
      const clientId = poolData.ClientId;
      
      const params = new URLSearchParams();
      params.append('grant_type', 'refresh_token');
      params.append('client_id', clientId);
      params.append('refresh_token', refreshToken);
      
      if (clientSecret) {
        params.append('client_secret', clientSecret);
      }
      
      const response = await axios.post(tokenEndpoint, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      // Store the new tokens
      const newIdToken = response.data.id_token;
      const newAccessToken = response.data.access_token;
      
      localStorage.setItem('idToken', newIdToken);
      localStorage.setItem('accessToken', newAccessToken);
      
      // Also update the Cognito-specific storage format
      localStorage.setItem(
        'CognitoIdentityServiceProvider.' + clientId + '.' + username + '.idToken', 
        newIdToken
      );
      localStorage.setItem(
        'CognitoIdentityServiceProvider.' + clientId + '.' + username + '.accessToken', 
        newAccessToken
      );
      
      return true;
    } catch (error) {
      console.error('Error refreshing token directly:', error);
      throw error;
    }
  }
  
  // Check if user is authenticated
  isAuthenticated() {
    const cognitoUser = userPool.getCurrentUser();
    const idToken = this.getIdToken();
    
    return new Promise((resolve) => {
      if (!cognitoUser || !idToken) {
        resolve(false);
        return;
      }
      
      // Check token expiration
      try {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = payload.exp - currentTime;
        
        
        if (payload.exp <= currentTime) {
          // Token expired, attempt refresh
          // Try to refresh the token before logging out
          this.refreshSession()
            .then(() => {
              // Successfully refreshed token
              resolve(true);
            })
            .catch(() => {
              // Failed to refresh token
              this.signOut();
              resolve(false);
            });
          return;
        }
        
        // If token is about to expire in the next hour, try to refresh it
        if (timeUntilExpiry < 3600) {
          // Token about to expire
          this.refreshSession()
            .then(() => {
              // Token refreshed
              resolve(true);
            })
            .catch(err => {
              console.error('Failed to refresh token:', err);
              // Continue with the current token if refresh fails
              cognitoUser.getSession((err, session) => {
                if (err || !session.isValid()) {
                  resolve(false);
                  return;
                }
                resolve(true);
              });
            });
          return;
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        resolve(false);
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err || !session.isValid()) {
          resolve(false);
          return;
        }
        resolve(true);
      });
    });
  }

  // Get access token
  getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  // Get ID token
  getIdToken() {
    return localStorage.getItem('idToken');
  }

  // Enable MFA
  enableMFA() {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession((err, session) => {
        if (err) {
          reject(err);
          return;
        }

        const mfaSettings = {
          PreferredMfa: true,
          Enabled: true
        };

        cognitoUser.setUserMfaPreference(null, mfaSettings, (err, result) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(result);
        });
      });
    });
  }
  
  // Forgot password - send reset code
  forgotPassword(email) {
    return new Promise((resolve, reject) => {
      const username = email;
      const userData = { Username: username, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);

      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          resolve(data);
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  // Confirm forgot password with code and new password
  confirmForgotPassword(email, verificationCode, newPassword) {
    return new Promise((resolve, reject) => {
      const username = email;
      const userData = { Username: username, Pool: userPool };
      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve({ success: true });
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  }

  // Create a test user for development purposes
  async createTestUser() {
    try {
      const result = await this.signUp('test@example.com', 'Password123!', 'Test', 'User');
      console.log('Test user created:', result);
      return result;
    } catch (error) {
      console.error('Error creating test user:', error);
      throw error;
    }
  }
  
  // Federated sign-in with social providers
  federatedSignIn(provider) {
    // Use the appropriate redirect URI based on environment
    const redirectUri = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:3000/' 
      : 'https://app.thejournalapp.com/';
    const encodedRedirectUri = encodeURIComponent(redirectUri);
    
    
    // Use the Cognito hosted UI with the correct domain and client ID
    window.location.href = `https://eu-west-1y9ybuyibo.auth.eu-west-1.amazoncognito.com/login?client_id=1tfcrcch96cov1jn36me1inhgl&response_type=code&scope=email+openid+profile&redirect_uri=${encodedRedirectUri}`;
    return { success: true };
  }
  
  // Exchange authorization code for tokens
  async exchangeAuthCode(code) {
    try {
      // Use the same redirect URI that was used for the initial authorization request
      const redirectUri = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3000/' 
        : 'https://app.thejournalapp.com/';
      
      
      // Use the correct Cognito domain format
      const tokenEndpoint = 'https://eu-west-1y9ybuyibo.auth.eu-west-1.amazoncognito.com/oauth2/token';
      const clientId = '1tfcrcch96cov1jn36me1inhgl';
      
      const params = new URLSearchParams();
      params.append('grant_type', 'authorization_code');
      params.append('client_id', clientId);
      params.append('code', code);
      params.append('redirect_uri', redirectUri);
      
      // If client secret is available, add the secret hash
      if (clientSecret) {
        const secretHash = calculateSecretHash(clientId);
        if (secretHash) {
          params.append('client_secret', clientSecret);
        }
      }
      
      const response = await axios.post(tokenEndpoint, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      
      // Map Cognito response to our expected format
      const tokenData = {
        idToken: response.data.id_token,
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token
      };
      
      // Store tokens
      localStorage.setItem('idToken', tokenData.idToken);
      localStorage.setItem('accessToken', tokenData.accessToken);
      if (tokenData.refreshToken) {
        localStorage.setItem('refreshToken', tokenData.refreshToken);
      }
      
      // Initialize the Cognito user session from the ID token
      try {
        // Parse the JWT to get the user's email/username
        const payload = JSON.parse(atob(tokenData.idToken.split('.')[1]));
        const username = payload.email || payload['cognito:username'] || payload.sub;
        
        if (username) {
          // Create a CognitoUser object and manually set the session
          const userData = { Username: username, Pool: userPool };
          const cognitoUser = new CognitoUser(userData);
          
          // Store the current user in localStorage (this is what getCurrentUser() checks)
          localStorage.setItem('CognitoIdentityServiceProvider.' + poolData.ClientId + '.LastAuthUser', username);
          
          // Store the tokens in the correct format for Cognito SDK
          localStorage.setItem(
            'CognitoIdentityServiceProvider.' + poolData.ClientId + '.' + username + '.idToken', 
            tokenData.idToken
          );
          localStorage.setItem(
            'CognitoIdentityServiceProvider.' + poolData.ClientId + '.' + username + '.accessToken', 
            tokenData.accessToken
          );
          if (tokenData.refreshToken) {
            localStorage.setItem(
              'CognitoIdentityServiceProvider.' + poolData.ClientId + '.' + username + '.refreshToken', 
              tokenData.refreshToken
            );
          }
          
          // Store the user in localStorage to maintain the session
          localStorage.setItem('cognito_email', username);
          
          // Session initialized successfully
        }
      } catch (sessionError) {
        console.error('Error initializing user session:', sessionError);
        // Continue anyway since we have the tokens
      }
      
      return tokenData;
    } catch (error) {
      console.error('Error exchanging auth code:', error);
      throw error;
    }
  }
}

export default new AuthService();